import { CalendarDotsIcon, ClockIcon, DownloadSimpleIcon } from '@phosphor-icons/react';
import { PageHeader } from 'components/PageHeader';
import { OrderLineStopType } from 'src/types/enums';
import React from 'react';
import useSWR from 'swr';
import { Link, useParams } from 'react-router-dom';
import { invariant, nullthrows } from '@utils/invariant';
import { fetchCustomerPortalCreditNote } from './models';

import { Breadcrumb } from 'components/Breadcrumb';
import { DataField } from 'components/DataField';
import { formatDate, formatInputTime } from '../../../utils/date';
import { getInvoiceTotals } from '../../../domain/invoice-totals';
import { decimateAndFormatNumber } from '../../../utils/number';
import { useTranslation } from '../../../contexts/translation-context';
import { PageHeading } from 'components/PageHeading';
import { Card } from 'components/Card';
import { useDownloadDocument } from 'src/app/document/hooks/useDownloadDocument';
import { Button } from 'components/button/Button';
import { DelayedSpinner } from 'components/Spinner';

export const CustomerPortalCreditNotePage = () => {
  const { creditNoteId } = useParams<{ creditNoteId: string }>();
  invariant(creditNoteId);
  const { i18n } = useTranslation();
  const { data, isLoading } = useSWR(['customer-portal-credit-note', creditNoteId], () =>
    fetchCustomerPortalCreditNote(creditNoteId),
  );
  const { downloadFile } = useDownloadDocument();

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  const creditNote = nullthrows(data, i18n('customerPortal.creditNotes.notFound'));
  const totals = getInvoiceTotals([
    ...creditNote.creditNoteOrders
      .map((o) =>
        o.lines
          .map((l) => {
            return {
              ...l,
              vatRateId: l.vatRate.id,
            };
          })
          .flat(),
      )
      .flat(),
    ...creditNote.manualLines.map((v) => {
      return {
        ...v,
        vatRateId: v.vatRate.id,
      };
    }),
  ]);

  const title = i18n('customerPortal.creditNotes.creditNote', {
    creditNoteNumber: creditNote.creditNoteNumber ?? i18n('draft'),
  });
  return (
    <>
      <PageHeader title={title} />

      <div>
        <PageHeading
          noMarginBottom
          leftSide={
            <Breadcrumb
              parentItem={{
                name: i18n('customerPortal.creditNotes.creditNotes'),
                to: '..',
              }}
              currentItem={title}
            />
          }
          rightSide={
            <Button
              color="primary"
              isDisabled={!creditNote.document}
              iconLeft={<DownloadSimpleIcon className="button-icon" />}
              onTrigger={() => (creditNote.document ? downloadFile(creditNote.document) : null)}
            >
              {i18n('download.download')}
            </Button>
          }
        />

        <div className="grid gap-4 p-7">
          <Card title={i18n('customerPortal.creditNotes.generalInformation')}>
            <div className="grid grid-cols-4 gap-4">
              <DataField title={i18n('customerPortal.creditNotes.structuredComment')}>
                {creditNote.structuredComment}
              </DataField>
              <DataField title={i18n('customerPortal.creditNotes.creditNoteDate')}>
                {formatDate(creditNote.date)}
              </DataField>
              {creditNote.sentAt && (
                <DataField title={i18n('customerPortal.creditNotes.sentAt')}>{formatDate(creditNote.sentAt)}</DataField>
              )}
              {creditNote.sentAt && creditNote.expiresAt && (
                <DataField title={i18n('customerPortal.creditNotes.dueDate')}>
                  {formatDate(creditNote.expiresAt)}
                </DataField>
              )}
            </div>
          </Card>

          {creditNote.creditNoteOrders.length > 0 && (
            <>
              <div className="heading-two">{i18n('customerPortal.orders.orders')}</div>

              {creditNote.creditNoteOrders.map((creditNoteOrder) => {
                const order = creditNoteOrder.order;

                const orderTotals = getInvoiceTotals(
                  creditNoteOrder.lines
                    .map((l) => {
                      return {
                        ...l,
                        vatRateId: l.vatRate.id,
                      };
                    })
                    .flat(),
                );

                return (
                  <div className="card flex flex-col gap-3.5 p-5" key={`order-${order.orderNumber}`}>
                    <div className="flex justify-between">
                      <Link to={`../../orders/${order.id}`} className="heading-four hover:text-brand-text">
                        {i18n('customerPortal.orders.orderLink', {
                          orderNumber: order.orderNumber ?? '',
                          customerRef: order.customerRef ?? '',
                        })}
                      </Link>
                    </div>

                    <div className="flex flex-col gap-3.5">
                      {order.lines.map((line) => {
                        return (
                          <div className="flex flex-col gap-3.5" key={`line-${line.id}`}>
                            <div className="flex flex-col gap-1.5">
                              {line.stops
                                .sort((a, b) => a.sequenceIndex - b.sequenceIndex)
                                .map((stop) => {
                                  return (
                                    <div className="grid grid-cols-4 gap-4 text-sm" key={`line-${line.id}-${stop.id}`}>
                                      <div className="font-medium">
                                        {stop.type === OrderLineStopType.Load
                                          ? i18n('customerPortal.creditNotes.load')
                                          : i18n('customerPortal.creditNotes.unload')}
                                      </div>

                                      <div className="text-fg-secondary">
                                        {stop.location.country}-{stop.location.name}
                                      </div>

                                      <div className="flex gap-2 items-center font-regular">
                                        <CalendarDotsIcon className="w-4 h-4" />
                                        {formatDate(stop.date)}
                                      </div>
                                      <div className="flex gap-2 items-center font-regular">
                                        <ClockIcon className="w-4 h-4" />
                                        {`${formatInputTime(stop.timeStart)}-${formatInputTime(stop.timeEnd)}`}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        );
                      })}

                      <div className="border-y border-border py-3.5 text-sm">
                        <div className="grid grid-cols-5 gap-x-4 mb-1">
                          <div className="label-text">{i18n('customerPortal.creditNotes.description')}</div>
                          <div className="label-text">{i18n('customerPortal.creditNotes.amount')}</div>
                          <div className="label-text">{i18n('customerPortal.creditNotes.unitPrice')}</div>
                          <div className="label-text">{i18n('customerPortal.creditNotes.total')}</div>
                          <div className="label-text">{i18n('customerPortal.creditNotes.vat')}</div>
                        </div>

                        <div className="grid grid-cols-5 gap-x-4 gap-y-1">
                          {creditNoteOrder.lines.map((line) => {
                            return (
                              <React.Fragment key={`creditnote-line-${line.id}`}>
                                <div>{line.productType.nameNl}</div>
                                <div className="whitespace-nowrap">
                                  {decimateAndFormatNumber(line.amount, 2, {
                                    decimalSeperator: ',',
                                  })}
                                </div>
                                <div className="whitespace-nowrap">
                                  €{' '}
                                  {decimateAndFormatNumber(line.unitPrice, 2, {
                                    decimalSeperator: ',',
                                  })}
                                </div>
                                <div className="whitespace-nowrap">
                                  €{' '}
                                  {decimateAndFormatNumber((line.amount * line.unitPrice) / 100, 2, {
                                    decimalSeperator: ',',
                                  })}
                                </div>
                                <div className="whitespace-nowrap">
                                  €{' '}
                                  {decimateAndFormatNumber(
                                    (line.amount * line.unitPrice * line.vatRatePercentage) / 1000000,
                                    2,
                                    {
                                      decimalSeperator: ',',
                                    },
                                  )}{' '}
                                  -{' '}
                                  {decimateAndFormatNumber(line.vatRatePercentage, 2, {
                                    decimalSeperator: ',',
                                  })}
                                  %
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>

                      <div className="text-sm font-medium whitespace-nowrap">
                        {i18n('customerPortal.creditNotes.orderTotal', {
                          total: decimateAndFormatNumber(orderTotals.totalExclVat, 2, {
                            decimalSeperator: ',',
                          }),
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {creditNote.manualLines.length > 0 && (
            <div className="card p-5 text-sm">
              <div className="grid grid-cols-5 gap-x-4 mb-1">
                <div className="label-text">{i18n('customerPortal.creditNotes.description')}</div>
                <div className="label-text">{i18n('customerPortal.creditNotes.amount')}</div>
                <div className="label-text">{i18n('customerPortal.creditNotes.unitPrice')}</div>
                <div className="label-text">{i18n('customerPortal.creditNotes.total')}</div>
                <div className="label-text">{i18n('customerPortal.creditNotes.vat')}</div>
              </div>

              <div className="grid grid-cols-5 gap-x-4 gap-y-1">
                {creditNote.manualLines.map((line) => {
                  return (
                    <React.Fragment key={`creditnote-line-${line.id}`}>
                      <div>{line.description}</div>
                      <div className="whitespace-nowrap">
                        {decimateAndFormatNumber(line.amount, 2, {
                          decimalSeperator: ',',
                        })}
                      </div>
                      <div className="whitespace-nowrap">
                        €{' '}
                        {decimateAndFormatNumber(line.unitPrice, 2, {
                          decimalSeperator: ',',
                        })}
                      </div>
                      <div className="whitespace-nowrap">
                        €{' '}
                        {decimateAndFormatNumber((line.amount * line.unitPrice) / 100, 2, {
                          decimalSeperator: ',',
                        })}
                      </div>
                      <div className="whitespace-nowrap">
                        €{' '}
                        {decimateAndFormatNumber((line.amount * line.unitPrice * line.vatRatePercentage) / 1000000, 2, {
                          decimalSeperator: ',',
                        })}{' '}
                        -{' '}
                        {decimateAndFormatNumber(line.vatRatePercentage, 2, {
                          decimalSeperator: ',',
                        })}
                        %
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <div className="grid w-[520px] grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
              <div className="label-text">{i18n('customerPortal.creditNotes.totalExclVat')}</div>
              <div className="whitespace-nowrap">
                €{' '}
                {decimateAndFormatNumber(totals.totalExclVat, 2, {
                  decimalSeperator: ',',
                })}
              </div>
              <div className="label-text">{i18n('customerPortal.creditNotes.totalVat')}</div>
              <div className="whitespace-nowrap">
                €{' '}
                {decimateAndFormatNumber(totals.totalVat, 2, {
                  decimalSeperator: ',',
                })}
              </div>
              <div className="label-text">{i18n('customerPortal.creditNotes.totalInclVat')}</div>
              <div className="whitespace-nowrap">
                €{' '}
                {decimateAndFormatNumber(totals.totalInclVat, 2, {
                  decimalSeperator: ',',
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
